'use client'
import { Suspense, useCallback, useEffect, useState, lazy, useRef } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Loader } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { colors } from "@/constant/colors";
import { Chapter } from "@/constant/schema";
import { useReward, addDiamondByElementType, costHeart, resetDemoReward } from "@/constant/reward";
import { startPractice, answerPractice, finishLesson, finishMistakesPractice, Task } from "@/constant/tasks";
import { ElementType, Lesson, Quest, ModeType, QuestionType, PracticeQuestion, AnswerRecord } from "@/constant/schema";

import useAuthStore from "@/store/useAuthStore";
import useRewardStore from "@/store/useRewardStore";
import useConfigStore from "@/store/useConfigStore";
import { useStreakStore } from "@/store/useStreakStore";

import Header from "@/components/Header";
import { DiamondFloatRef } from "@/components/Animation/diamondFloatAnimation";
import { runDiamondAnimation } from "@/components/Animation/diamondFlyAnimation";
import PracticeNode from "@/components/PracticeCard/practice-node";
import { useFinishTask } from '@/components/Confetti/FinishTask';
import NormalButton from "@/components/Button/normal";
import { useOverlay } from "@/components/Provider/OverlayProvider";
import {
  PublicStudyAuthPromptModal,
  PublicStudyAuthPromptProvider,
  type PublicStudyAuthPromptReason,
} from "@/components/public-study/PublicStudyAuthPrompt";
import MarkdownComponent from "@/components/Markdown";
import { AssistantAvatar, LetterAvatar } from "@/components/Avatar";
import Image from "@/components/Image";

import { capitalizeFirstLetter, isLearnTest } from "@/lib/utils";
import { getQuestById } from "@/lib/questService";
import UserTrackService from "@/lib/UserTrackService";
import report, { ReportEvent } from "@/lib/report";
import UserQuestProgressService from "@/lib/userQuestProgressService";
import { playCorrectSound, playWrongSound } from "@/lib/systemSound";
import { getPracticeById, transferAnswersToAnswerRecords, insertAnswer, updatePracticeProgress, getQuestionsByIds, fetchAnswersFromUserAnswers } from "@/lib/practiceHelper";
import LearnInput from "@/components/LearnCard/learn-input";
import {
  isPracticeQuestionLiveTutorEnabled,
  isPracticeQuestionLiveTutorSupported,
} from "@/components/LearnCard/live-tutor-selection";
import supabase from "@/lib/supabase";
import { apiClient } from "@/lib/api-client";
import useAudioStore from "@/store/useAudioStore";
import { useDeviceType } from "@/lib/hooks";
import { createFeedActivity } from '@/lib/feedActivityService';
import { isParentProfile, isStudentProfile } from "@/lib/profile-role";
import { checkSubjectAccess } from '@/hooks/useSubjectGate';
import SubjectManagementModal from '@/components/SubjectManagementModal';
import ConfirmSubjectSelectionModal from '@/components/ConfirmSubjectSelectionModal';
import CanvasPlayerMini from '@/app/magic-lab/components/CanvasPlayerMini';
import { CanvasAnimationData } from '@/app/magic-lab/types';

// Lazy load components that are controlled by state
const HeartBuyPopup = lazy(() => import("@/components/Popup/HeartBuyPopup"));
const CustomFeedbackSurvey = lazy(() => import('@/components/CustomFeedbackSurvey'));
const ReviewPracticeModal = lazy(() => import('@/components/Popup/ReviewPracticeModal'));

interface PracticeMessage {
  role: 'user' | 'assistant',
  content: string,
}

function normalizePracticeSubjectKey(subject: string | null | undefined): string {
  const normalized =
    typeof subject === 'string'
      ? subject.trim().toLowerCase().replace(/[\s-]+/g, '_')
      : '';
  if (
    normalized === 'social_studies' ||
    normalized === 'socialstudy' ||
    normalized === 'socialstudies'
  ) {
    return 'history';
  }
  return normalized;
}

type PracticeClientProps = {
  publicQuestId?: string | null;
  publicChapterId?: string | null;
  publicLessonId?: string | null;
  publicMode?: 'problemSet' | 'challenge' | null;
  publicGuestMode?: boolean;
  publicSkillId?: string | null;
  publicSkillTitle?: string | null;
  publicSkillQuestions?: PracticeQuestion[] | null;
  publicSkillParentTitle?: string | null;
};

const PracticeContent = ({
  publicQuestId = null,
  publicChapterId = null,
  publicLessonId = null,
  publicMode = null,
  publicGuestMode = false,
  publicSkillId = null,
  publicSkillTitle = null,
  publicSkillQuestions = null,
  publicSkillParentTitle = null,
}: PracticeClientProps) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const reward = useReward();
  const user = useAuthStore(state => state.user);
  const profile = useAuthStore(state => state.profile);
  const { buyHeartPopup, setDemoRewardMode } = useRewardStore();
  const playTTS = useAudioStore(state => state.playTTS);
  const interruptAudio = useAudioStore(state => state.interruptAudio);

  const quest_id = publicQuestId || searchParams.get('quest_id') as string;
  const chapter_id = publicChapterId || searchParams.get('chapter_id') as string;
  const lesson_id = publicLessonId || searchParams.get('lesson_id') as string;
  const practice_id = searchParams.get('practice_id') as string;
  const type = publicMode || searchParams.get('type') as string;
  const from = searchParams.get('from') as string;
  const modeType = type === 'challenge' ? ModeType.CHALLENGE : type === 'problemSet' ? ModeType.PROBLEM_SET : lesson_id ? ModeType.PRACTICE : practice_id ? ModeType.MISTAKE : ModeType.TEST;
  const forceGuestPreview = searchParams.get('preview') === '1';
  const isGuestMode = publicGuestMode && !user;
  const isParentPreviewMode = Boolean(user && publicGuestMode && forceGuestPreview && isParentProfile(profile));
  const isStudentMode = isStudentProfile(profile);
  const isPublicSkillPractice = Boolean(publicSkillId && publicSkillQuestions?.length);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [authPromptReason, setAuthPromptReason] = useState<PublicStudyAuthPromptReason>('ask-pengi');
  const hasPromptedAfterQuestionRef = useRef(false);

  const [trackingInterval, setTrackingInterval] = useState<NodeJS.Timeout | null>(null);
  const [loading, setLoading] = useState(false);
  const [quest, setQuest] = useState<Quest | null>(null);
  const [showAllChapters, setShowAllChapters] = useState(false);
  const [questInfo, setQuestInfo] = useState<any>({});
  const [quizQuestions, setQuizQuestions] = useState<PracticeQuestion[]>([]);
  const [maxProgressIndex, setMaxProgressIndex] = useState<number>(0);
  const [allFinished, setAllFinished] = useState<boolean>(false);
  const [taskPracticeId, setTaskPracticeId] = useState<string | null>(null);
  const [initialProgress, setInitialProgress] = useState<number>(0);
  const [initialOptions, setInitialOptions] = useState<AnswerRecord[]>([]);
  const [subject, setSubject] = useState<string | null>(null);
  const [subjectPaywallSubject, setSubjectPaywallSubject] = useState<string | null>(null);
  const [confirmSubjectBeforeStart, setConfirmSubjectBeforeStart] = useState<string | null>(null);
  const completeToday = useStreakStore(state => state.completeToday);

  const diamondFloatRef = useRef<DiamondFloatRef>(null);
  const currentDiamondImageRef = useRef<any>(null);
  const totalDiamondImageRef = useRef<any>(null);
  const hasShownMistakeTransitionRef = useRef<boolean>(false);
  const { showTaskToast } = useFinishTask();
  const betterExperienceTip = useConfigStore(state => state.betterExperienceTip);
  const checkShowBetterExperienceTip = useConfigStore(state => state.checkShowBetterExperienceTip);

  const [comboCount, setComboCount] = useState<number>(0);
  const [showCelebration, setShowCelebration] = useState<boolean>(false);
  const [celebrationAnimationData, setCelebrationAnimationData] = useState<CanvasAnimationData | null>(null);
  const lastCelebrationComboRef = useRef<number>(0);

  // Practice state management
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [currentAnswer, setCurrentAnswer] = useState<any>({ selectedOption: null as string | null, isCorrect: null as boolean | null });
  const [currentQuestion, setCurrentQuestion] = useState<PracticeQuestion>();
  const [answerRecords, setAnswerRecords] = useState<AnswerRecord[]>([]);
  const [currentQuestions, setCurrentQuestions] = useState<PracticeQuestion[]>([]);
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [reviewAnswer, setReviewAnswer] = useState<boolean>(false);
  const [showReportPopup, setShowReportPopup] = useState(false);
  const [pendingUserInput, setPendingUserInput] = useState<string>("");
  const [triggerCheck, setTriggerCheck] = useState<boolean>(false);
  const [showMistakeTransition, setShowMistakeTransition] = useState<boolean>(false);

  const { fireConfetti } = useOverlay()

  const [showInput, setShowInput] = useState(false);
  const [messages, setMessages] = useState<PracticeMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesRef = useRef<HTMLDivElement>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Review mode states
  const [reviewMode, setReviewMode] = useState<boolean>(false);
  const [canReview, setCanReview] = useState<boolean>(false);
  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const hasShownCompletionRef = useRef<boolean>(false);

  const deviceType = useDeviceType();
  const baseHeight = deviceType === 'tablet' ? 60 : 100;

  const requestAuthPrompt = useCallback((reason: PublicStudyAuthPromptReason) => {
    if (!isGuestMode) {
      return false;
    }
    setAuthPromptReason(reason);
    setShowAuthPrompt(true);
    return true;
  }, [isGuestMode]);

  useEffect(() => {
    if (isParentPreviewMode && !isStudentMode) {
      resetDemoReward();
    }
    setDemoRewardMode(isParentPreviewMode && !isStudentMode);
    return () => {
      setDemoRewardMode(false);
    };
  }, [isParentPreviewMode, isStudentMode, setDemoRewardMode]);

  // ==================== UseEffects 区域 ====================

  // 加载庆祝动画数据 (id=16)
  useEffect(() => {
    const loadCelebrationAnimation = async () => {
      try {
        // 从数据库查询id=16的动画
        const { data: animation, error } = await supabase
          .from('pengi_animate')
          .select('*')
          .eq('id', 16)
          .eq('status', 'completed')
          .single();

        if (error || !animation) {
          console.error('Failed to load celebration animation:', error);
          return;
        }

        // 加载bundle数据
        const response = await fetch(animation.bundle_url);
        const bundle = await response.json();

        // 使用默认动作或第一个动作
        const actionName = bundle.defaultAction || bundle.actions[0].name;
        const action = bundle.actions.find((a: any) => a.name === actionName);

        if (action) {
          const canvasData: CanvasAnimationData = {
            sessionId: animation.session_id,
            frames: action.frames.map((base64: string) => `data:${action.mimeType};base64,${base64}`),
            fps: action.fps,
            width: action.width,
            height: action.height,
            audioUrl: action.audioBase64 ? `data:audio/mpeg;base64,${action.audioBase64}` : null,
            hasAudio: !!action.audioBase64,
          };
          setCelebrationAnimationData(canvasData);
        }
      } catch (error) {
        console.error('Error loading celebration animation:', error);
      }
    };

    loadCelebrationAnimation();
  }, []);

  // 监听comboCount变化，触发庆祝动画
  useEffect(() => {
    // 条件：comboCount >= 2 且比上次触发时增加了（即新答对了一题）
    if (comboCount >= 5 && celebrationAnimationData && comboCount > lastCelebrationComboRef.current) {
      lastCelebrationComboRef.current = comboCount;
      setShowCelebration(true);

      // 4秒后自动隐藏
      const timer = setTimeout(() => {
        setShowCelebration(false);
      }, 4000);

      return () => clearTimeout(timer);
    }

    // 当comboCount重置为0时，重置lastCelebrationComboRef
    if (comboCount === 0) {
      lastCelebrationComboRef.current = 0;
    }
  }, [comboCount, celebrationAnimationData]);

  // 初始化题目和进度
  useEffect(() => {
    if (quizQuestions.length > 0) {
      // 根据之前的答题记录重建题目顺序
      const reorderedQuestions = reconstructQuestionOrder(quizQuestions, initialOptions || []);
      setCurrentQuestions(reorderedQuestions);

      if (initialOptions?.length > 0) {
        setAnswerRecords(initialOptions.filter(record => quizQuestions.find(question => question.id === record.questionId)));

        // 如果所有题目都已答过，标记过渡页面已显示
        if (initialOptions.length === quizQuestions.length) {
          hasShownMistakeTransitionRef.current = true;
        }
      }

      // 使用重新排序后的题目列表来初始化index
      const currentIdx = initCurrentQuestionIndex(reorderedQuestions, initialOptions || []);
      setCurrentQuestionIndex(currentIdx);
      setCurrentQuestion(reorderedQuestions[currentIdx]);

      if (initialProgress === 100) {
        setAllFinished(true);
        setShowSummary(true);
        setCanReview(true); // 当初始进度为100时，允许review
      } else {
        setAllFinished(false);
        setShowSummary(false);
        setReviewAnswer(false);
        setCanReview(false);
      }
    }
  }, [quizQuestions, initialOptions, initialProgress]);

  // 答案记录变化时更新进度
  useEffect(() => {
    if (answerRecords?.length > 0) {
      updateUserAnswer(answerRecords);
      const correctCount = answerRecords.filter(record => record.isCorrect).length;
      setMaxProgressIndex(correctCount);
      const progress = Math.ceil(correctCount / currentQuestions.length * 100);
      updateUserProgress(progress);
      if (progress === 100) {
        setAllFinished(true);
      }
    }
  }, [answerRecords]);

  // 保存答案到数据库
  useEffect(() => {
    const saveAnswer = async () => {
      // 在review模式下不保存答案
      if (reviewMode) {
        console.log('🔄 Review mode: skipping answer save to database');
        return;
      }

      if (currentAnswer.selectedOption !== null && currentQuestion) {
        const answerId = await insertAnswer(
          currentQuestion.id,
          user?.id || null,
          currentAnswer.isCorrect,
          currentAnswer.selectedOption,
          modeType,
          lesson_id || chapter_id,
          subject || ''
        );
        if (practice_id) {
          const progress = getProgress();
          updatePracticeProgress(practice_id, progress, answerId);
        }
      }
    };
    saveAnswer();
  }, [currentAnswer, reviewMode]);

  // 加载当前题目的聊天记录
  const loadChatMessages = async () => {
    // 在 practice_id 存在时不支持聊天功能，直接返回
    if (practice_id) {
      setMessages([]);
      setLoadingMessages(false);
      return;
    }

    if (!currentQuestion?.id || !user?.id) {
      setMessages([]);
      setLoadingMessages(false);
      return;
    }

    try {
      setLoadingMessages(true);
      const { data, error } = await supabase
        .from('user_practice_chat')
        .select('messages')
        .eq('user_id', user.id)
        .eq('question_id', currentQuestion.id)
        .eq('quest_id', quest_id || '')
        .eq('chapter_id', chapter_id || '')
        .eq('lesson_id', lesson_id || '')
        .maybeSingle();

      if (data?.messages) {
        setMessages(data.messages as PracticeMessage[]);
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to load chat messages:', error);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // 切换题目时滚动到顶部和加载聊天记录
  useEffect(() => {
    // 立即跳转到顶部，避免和卡片动画冲突导致抖动
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });

    loadChatMessages();
  }, [currentQuestionIndex, currentQuestion?.id]);

  // 监听虚拟键盘状态
  useEffect(() => {
    let lastKeyboardHeight = 0;

    const scrollToInputIfNeeded = (keyboardHeight: number) => {
      // 只在键盘弹起时执行
      if (keyboardHeight <= 0) {
        return;
      }

      // 查找 MathInput 输入框元素 - 尝试多个选择器
      let mathInputElement = document.querySelector('.ML__field') as HTMLElement;

      if (!mathInputElement) {
        // 尝试其他可能的选择器
        mathInputElement = document.querySelector('math-field') as HTMLElement;
      }

      if (!mathInputElement) {
        mathInputElement = document.querySelector('[class*="math-field"]') as HTMLElement;
      }

      // 获取输入框的位置信息
      const inputRect = mathInputElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // 底部按钮区域的高度（Continue按钮区域）
      const footerHeight = 80;

      // 计算可用的可视区域顶部位置（视口高度 - 键盘高度 - 底部按钮高度）
      const availableBottom = viewportHeight - keyboardHeight - footerHeight;

      // 检查输入框是否被遮挡（只要输入框的任何部分被遮挡就需要滚动）
      const bufferSpace = 20; // 输入框底部的额外缓冲空间
      const isInputCovered = inputRect.bottom > (availableBottom - bufferSpace);

      if (isInputCovered) {
        // 计算需要滚动的距离，让整个输入框（56px高度）都在可见区域内
        // 目标：输入框底部应该在 availableBottom - bufferSpace 的位置
        const targetInputBottom = availableBottom - bufferSpace;

        // 当前输入框底部超出目标位置的距离
        const overflowDistance = inputRect.bottom - targetInputBottom;

        // 向上滚动这个距离
        const targetScrollY = window.scrollY + overflowDistance;

        // 平滑滚动到目标位置
        window.scrollTo({
          top: Math.max(0, targetScrollY),
          behavior: 'smooth'
        });
      } else {
        console.log('✅ Input is already visible, no scroll needed');
      }
    };

    const updateKeyboardHeight = () => {
      if (window.mathVirtualKeyboard && window.mathVirtualKeyboard.visible) {
        const keyboardElement = window.mathVirtualKeyboard.boundingRect;
        if (keyboardElement) {
          const newHeight = keyboardElement.height;

          // 只有当高度真正变化时才更新和滚动，避免无限循环
          if (newHeight !== lastKeyboardHeight) {
            console.log('⌨️ Keyboard height updated:', newHeight);
            lastKeyboardHeight = newHeight;
            setKeyboardHeight(newHeight);

            // 延迟检查并滚动，确保DOM已更新
            setTimeout(() => {
              scrollToInputIfNeeded(newHeight);
            }, 100);

            // 再次延迟检查（备用）
            setTimeout(() => {
              scrollToInputIfNeeded(newHeight);
            }, 300);
          }
        }
      } else {
        if (lastKeyboardHeight > 0) {
          console.log('⌨️ Keyboard hidden');
          lastKeyboardHeight = 0;
          setKeyboardHeight(0);
        }
      }
    };

    // 使用 MutationObserver 监听虚拟键盘 DOM 变化
    const observer = new MutationObserver(() => {
      updateKeyboardHeight();
    });

    // 监听键盘容器
    const observeKeyboard = () => {
      const keyboardContainer = document.querySelector('.ML__keyboard');
      if (keyboardContainer) {
        observer.observe(keyboardContainer, {
          attributes: true,
          attributeFilter: ['style', 'class'],
          childList: true,
          subtree: true,
        });
      } else {
        console.log('❌ Keyboard container not found');
      }
    };

    // 延迟观察，确保键盘已加载
    setTimeout(observeKeyboard, 500);

    // 定期检查键盘高度（备用方案）
    const interval = setInterval(updateKeyboardHeight, 100);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  // 页面卸载时退出review模式
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Exit review mode when page unloads
      if (reviewMode) {
        exitReviewMode();
      }
    };

    // 添加页面关闭事件监听
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // 执行清理 (包括退出 review mode)
      handleBeforeUnload();
    };
  }, [reviewMode]);

  // 键盘事件处理器
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // 不处理Enter键如果:
      // 1. 聊天输入框正在显示
      // 2. 当前焦点在输入框内（检查是否在input或textarea内）
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.getAttribute('contenteditable') === 'true'
      );

      if (showInput || isInputFocused) {
        return; // 不处理任何键盘事件
      }

      // 处理回车键：
      // 1. 显示错题过渡页面时，按回车继续到下一题
      // 2. 已选择答案时，按回车继续到下一题
      if (event.key === 'Enter' && (showMistakeTransition || currentAnswer.selectedOption !== null)) {
        event.preventDefault();
        handleNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentAnswer.selectedOption, showInput, showMistakeTransition]);

  // ==================== 方法区域 ====================

  // 根据答题记录重建题目顺序
  const reconstructQuestionOrder = (
    questions: PracticeQuestion[],
    answerRecords: AnswerRecord[]
  ): PracticeQuestion[] => {
    if (!answerRecords || answerRecords.length === 0) {
      return questions; // 首次开始，返回原始顺序
    }

    const answerMap = new Map<string, AnswerRecord>();
    answerRecords.forEach(record => answerMap.set(record.questionId, record));

    const unanswered: PracticeQuestion[] = [];
    const correct: PracticeQuestion[] = [];
    const wrong: PracticeQuestion[] = [];

    questions.forEach(question => {
      const record = answerMap.get(question.id);
      if (!record) {
        unanswered.push(question);
      } else if (record.isCorrect) {
        correct.push(question);
      } else {
        wrong.push(question);
      }
    });

    const allAnswered = answerRecords.length === questions.length;

    // 如果全部答过：错题在前（错题回顾阶段）
    // 否则：未答在前，正确在中间，错误在最后
    return allAnswered ? [...wrong, ...correct] : [...unanswered, ...correct, ...wrong];
  };

  // 进入review模式
  const enterReviewMode = () => {
    console.log('🔄 Entering practice review mode');

    // 1. 设置review模式标志
    setReviewMode(true);

    // 2. 重置所有状态
    setCurrentQuestionIndex(0);
    setCurrentQuestion(quizQuestions[0]);
    setCurrentAnswer({ selectedOption: null, isCorrect: null });
    setAnswerRecords([]);
    setComboCount(0);
    setPendingUserInput('');
    setTriggerCheck(false);
    setShowSummary(false);
    setReviewAnswer(false);
    setShowMistakeTransition(false);
    setAllFinished(false);
    setMaxProgressIndex(0);
    hasShownMistakeTransitionRef.current = false;

    // 3. 清空聊天消息
    setMessages([]);
    setShowInput(false);

    console.log('✅ Review mode started, all progress reset');
  };

  // 退出review模式
  const exitReviewMode = () => {
    if (!reviewMode) return;

    console.log('🔄 Exiting practice review mode');
    setReviewMode(false);

    // 恢复到完成状态
    if (canReview) {
      setAllFinished(true);
      setShowSummary(true);
    }

    console.log('✅ Review mode exited');
  };

  // 滚动到底部函数
  const scrollToBottom = () => {
    if (messagesRef.current) {
      // Use requestAnimationFrame to ensure DOM has been updated
      requestAnimationFrame(() => {
        const messagesBottom = messagesRef.current!.getBoundingClientRect().bottom + window.scrollY;
        // Subtract footer height (80px) to ensure content is visible above footer
        const targetScrollTop = messagesBottom - window.innerHeight + 130; // 130px buffer for footer with input
        window.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth'
        });
      });
    }
  }

  // 检查题目是否首次答错
  const isFirstAnswerWrong = (questionId: string) => {
    const answerRecord = answerRecords.find(record => record.questionId === questionId);
    if (!answerRecord) return false;
    // 检查首次答题结果，如果 firstIsCorrect 不存在，fallback 到 isCorrect
    return answerRecord.firstIsCorrect !== undefined
      ? !answerRecord.firstIsCorrect
      : !answerRecord.isCorrect;
  }

  const currentQuestionWasWrongInitially = currentQuestion
    ? isFirstAnswerWrong(currentQuestion.id)
    : false;

  const isPracticeLikeMode = modeType === ModeType.PRACTICE || modeType === ModeType.MISTAKE;
  const liveTutorModeForEligibility = modeType === ModeType.MISTAKE ? ModeType.PRACTICE : modeType;
  const hasAnsweredCurrentQuestion = currentAnswer.selectedOption !== null || currentAnswer.isCorrect !== null;
  const normalizedPracticeSubject = normalizePracticeSubjectKey(subject || quest?.subject);
  const hasUnknownPracticeSubject = normalizedPracticeSubject.length === 0;
  const isPracticeLiveTutorSubjectEnabled =
    isPracticeQuestionLiveTutorEnabled(liveTutorModeForEligibility, subject || quest?.subject) ||
    (liveTutorModeForEligibility === ModeType.PRACTICE && hasUnknownPracticeSubject);
  const isCurrentQuestionLiveTutorSupported = isPracticeQuestionLiveTutorSupported(currentQuestion?.type);
  const shouldShowRetryLiveTutor =
    isPracticeLikeMode &&
    hasAnsweredCurrentQuestion &&
    isPracticeLiveTutorSubjectEnabled &&
    isCurrentQuestionLiveTutorSupported;

  // 获取当前进度百分比
  const getProgress = () => {
    const correctCount = answerRecords.filter(record => record.isCorrect).length;
    return Math.ceil(correctCount / currentQuestions.length * 100);
  }

  // 初始化当前题目索引（基于重新排序后的题目列表）
  const initCurrentQuestionIndex = (questions: PracticeQuestion[], answers: AnswerRecord[]) => {
    if (!answers || answers.length === 0) {
      return 0;
    }

    const allAnswered = answers.length === questions.length;

    if (allAnswered) {
      // 全部答过：查找第一个错题（经过重排序后错题在最前面）
      for (let i = 0; i < questions.length; i++) {
        const record = answers.find(r => r.questionId === questions[i].id);
        if (record && !record.isCorrect) {
          return i;
        }
      }
      return 0;
    } else {
      // 未全部答过：查找第一个未答题（经过重排序后未答题在最前面）
      for (let i = 0; i < questions.length; i++) {
        const record = answers.find(r => r.questionId === questions[i].id);
        if (!record) {
          return i;
        }
      }
      return 0;
    }
  }

  // 处理用户答题
  const handleAnswer = (option: string, isCorrect: boolean, autoSubmit: boolean = false) => {
    if (currentQuestionIndex >= currentQuestions.length) {
      return;
    }

    // 对于需要Check的题目，如果是autoSubmit，则直接处理答案
    // 对于不需要Check的题目（如Quiz），立即处理答案
    if (autoSubmit || currentQuestion?.type === QuestionType.MULTIPLE_CHOICE || currentQuestion?.type === QuestionType.GRAMMAR_CLASSIFICATION) {
      setCurrentAnswer({ selectedOption: option, isCorrect: isCorrect });
      // setHelperContext({
      //   type: isCorrect ? "correct" : "incorrect",
      //   message: {
      //     question: currentQuestion?.question,
      //     answer: option,
      //     correctAnswer: currentQuestion?.answer
      //   }
      // });

      // 创建答案记录
      const answerRecord: AnswerRecord = {
        questionId: currentQuestions[currentQuestionIndex].id,
        selectedOption: option,
        isCorrect: isCorrect,
        timestamp: Date.now(),
      };

      // 更新答案记录
      setAnswerRecords(prev => {
        const existingIndex = prev.findIndex(record => record.questionId === answerRecord.questionId);
        if (existingIndex !== -1) {
          // 更新已存在的记录（重做）
          const updated = [...prev];
          answerRecord.firstSelectedOption = prev[existingIndex].firstSelectedOption;
          answerRecord.firstIsCorrect = prev[existingIndex].firstIsCorrect;
          updated[existingIndex] = answerRecord;
          return updated;
        } else {
          // 添加新记录（首次答题）
          answerRecord.firstSelectedOption = option;
          answerRecord.firstIsCorrect = isCorrect;
          return [...prev, answerRecord];
        }
      });

      // 如果答错，将这道题移到数组末尾（不论是首次还是重做）
      if (!isCorrect) {
        setCurrentQuestions(prev => {
          const newQuestions = [...prev];
          const wrongQuestion = newQuestions[currentQuestionIndex];
          // 移除当前位置的题目
          newQuestions.splice(currentQuestionIndex, 1);
          // 添加到末尾
          newQuestions.push(wrongQuestion);
          return newQuestions;
        });
      }

      if (isCorrect) {
        setComboCount(c => c + 1);
        playCorrectSound();
      } else {
        setComboCount(0);
        playWrongSound();
      }
      doReward(isCorrect);
      if (!hasPromptedAfterQuestionRef.current && requestAuthPrompt('complete-question')) {
        hasPromptedAfterQuestionRef.current = true;
      }
    } else {
      // 对于需要Check的题目，先暂存答案但不处理
      setCurrentAnswer({ selectedOption: option, isCorrect: null });
    }
  }

  // 处理下一题按钮点击（同时处理Check和Next逻辑）
  const handleNext = async (): Promise<void> => {
    setShowInput(false);

    // 如果正在显示错题过渡页面，点击 Continue 后开始做错题
    if (showMistakeTransition) {
      setShowMistakeTransition(false);
      // 找到第一道答错的题目（错题在数组中可能不在开头，需要查找）
      const firstWrongIndex = currentQuestions.findIndex(q => {
        const record = answerRecords.find(r => r.questionId === q.id);
        return record && !record.isCorrect;
      });
      const nextIndex = firstWrongIndex >= 0 ? firstWrongIndex : 0;
      setCurrentQuestionIndex(nextIndex);
      setCurrentQuestion(currentQuestions[nextIndex]);
      setCurrentAnswer({ selectedOption: null, isCorrect: null });
      setPendingUserInput('');
      setTriggerCheck(false);
      return;
    }

    // 判断是否需要Check（非Quiz类题目且还未判断对错）
    const needsCheck = currentQuestion?.type !== QuestionType.MULTIPLE_CHOICE &&
      currentQuestion?.type !== QuestionType.GRAMMAR_CLASSIFICATION &&
      pendingUserInput !== '' &&
      currentAnswer.isCorrect === null;

    if (needsCheck) {
      // 执行Check逻辑 - 触发卡片内部的submit
      setTriggerCheck(true);
      // 在下一个渲染周期重置triggerCheck
      setTimeout(() => setTriggerCheck(false), 100);
      return;
    }

    interruptAudio();

    const correctCount = answerRecords.filter(record => record.isCorrect).length;

    if (correctCount < currentQuestions.length && currentAnswer.selectedOption !== null) {
      // 检查是否所有题目都已经做过（无论对错）
      const allQuestionsAnswered = answerRecords.length === currentQuestions.length;
      // 检查是否有错题
      const hasWrongAnswers = answerRecords.some(record => !record.isCorrect);

      // 关键时间点：所有题目都做过了，但还有错题
      // 这意味着用户刚做完一轮题目，准备开始做错题了
      // 使用 ref 确保在同一个 practice session 中只显示一次过渡页面
      if (allQuestionsAnswered && hasWrongAnswers && !hasShownMistakeTransitionRef.current) {
        // 显示过渡页面
        setShowMistakeTransition(true);
        hasShownMistakeTransitionRef.current = true;
        setMessages([]);
        setLoadingMessages(false);  
        return;
      }

      // 跳转到下一题
      let nextIndex: number;

      // 如果当前题答错了，题目已经被移到末尾，保持在当前索引
      if (!currentAnswer.isCorrect) {
        nextIndex = currentQuestionIndex;
      } else {
        // 如果答对了，移到下一题
        nextIndex = currentQuestionIndex + 1;
      }

      setCurrentQuestionIndex(nextIndex);
      setCurrentQuestion(currentQuestions[nextIndex]);
      setCurrentAnswer({ selectedOption: null, isCorrect: null });
      setPendingUserInput(''); // 重置输入
      setTriggerCheck(false); // 重置触发器
      // 注意：不需要手动调用 loadChatMessages()，因为 useEffect 会在 currentQuestion?.id 变化时自动调用
      return;
    }

    // 完成所有题目，显示总结
    if (correctCount === currentQuestions.length && !showSummary) {
      setShowInput(false);
      setMessages([]);

      // 第一次完成时设置canReview
      if (!hasShownCompletionRef.current && !reviewMode && user) {
        setCanReview(true);
        hasShownCompletionRef.current = true;
      }

      fireConfetti();
      const firstCorrectCount = answerRecords.filter(record => record.firstIsCorrect).length;
      finishPractice(firstCorrectCount / currentQuestions.length);
      setShowSummary(true);
      report(ReportEvent.USER_FINISH_PRACTICE, {
        quest_id: quest_id,
        chapter_id: chapter_id,
        lesson_id: lesson_id,
      });
      return;
    }
  };

  // 打开答案回顾
  const handleReviewAnswers = () => {
    setShowSummary(false);
    setReviewAnswer(true);
  };

  // 关闭答案回顾
  const handleReviewClose = () => {
    setReviewAnswer(false);
    setShowSummary(true);
  };

  // 判断Next/Check按钮是否禁用
  const isNextButtonDisabled = () => {
    if (reward.heart <= 0) {
      return false;
    }
    if (allFinished) {
      return showSummary ? currentQuestionIndex === currentQuestions.length - 1 : false;
    } else {
      // 对于需要Check的题目，需要有输入
      const needsCheck = currentQuestion?.type !== QuestionType.MULTIPLE_CHOICE &&
        currentQuestion?.type !== QuestionType.GRAMMAR_CLASSIFICATION;
      if (needsCheck) {
        return pendingUserInput === '' && currentAnswer.selectedOption === null;
      }
      // 对于Quiz类题目，需要已选择答案
      return currentAnswer.selectedOption === null;
    }
  };

  // 获取按钮文本
  const getButtonText = () => {
    const needsCheck = currentQuestion?.type !== QuestionType.MULTIPLE_CHOICE &&
      currentQuestion?.type !== QuestionType.GRAMMAR_CLASSIFICATION &&
      pendingUserInput !== '' &&
      currentAnswer.isCorrect === null;

    // 如果需要 Check，显示 Check
    if (needsCheck) {
      return "Check";
    }

    // 如果已经判断过且答案错误，显示 Got it
    if (currentAnswer.isCorrect === false) {
      return "Got it";
    }

    // 其他情况显示 Continue
    return "Continue";
  };

  // 获取按钮颜色类型
  const getButtonColorType = () => {
    // 如果已经判断过且答案错误，使用红色
    if (currentAnswer.isCorrect === false) {
      return "red";
    }
    // 其他情况使用默认颜色
    return undefined;
  };

  // 心形值为0时显示购买弹窗
  useEffect(() => {
    if (reward.heart === 0) {
      useRewardStore.setState({ buyHeartPopup: true });
    }
  }, [reward]);

  // 自动滚动到底部 - 只在聊天激活时滚动
  useEffect(() => {
    if (!showInput) {
      return;
    }

    // Auto-scroll to bottom using window scroll, accounting for fixed footer
    setTimeout(() => {
      if (messagesRef.current) {
        scrollToBottom();
      }
    }, 100);

    // Backup scroll to ensure it works
    setTimeout(() => {
      if (messagesRef.current) {
        scrollToBottom();
      }
    }, 500);

    // Additional scroll after layout is complete
    setTimeout(() => {
      if (messagesRef.current) {
        scrollToBottom();
      }
    }, 1000);
  }, [messages, showInput]);

  // 初始化页面数据
  useEffect(() => {
    // 清除之前课程的状态
    const clearPreviousState = () => {
      // 退出review模式
      if (reviewMode) {
        console.log('🔄 URL changed, exiting review mode');
        setReviewMode(false);
      }

      // 重置review相关状态
      setCanReview(false);
      setShowReviewModal(false);
      hasShownCompletionRef.current = false;

      // 清除答题相关状态
      setCurrentQuestionIndex(0);
      setCurrentAnswer({ selectedOption: null, isCorrect: null });
      setCurrentQuestion(undefined);
      setAnswerRecords([]);
      setCurrentQuestions([]);
      setPendingUserInput('');
      setTriggerCheck(false);

      // 清除进度相关状态
      setMaxProgressIndex(0);
      setAllFinished(false);
      setShowSummary(false);
      setReviewAnswer(false);
      setShowReportPopup(false);
      setShowMistakeTransition(false);
      // hasShownMistakeTransitionRef 不在这里重置，会在初始化useEffect中根据答题记录正确设置

      // 清除连击和任务状态
      setComboCount(0);
      setTaskPracticeId(null);

      // 清除辅助状态
      // setHelperContext({});

      // 清除聊天相关状态
      setMessages([]);
      setShowInput(false);

      // 清除题目和选项
      setQuizQuestions([]);
      setInitialProgress(0);
      setInitialOptions([]);

      setShowAllChapters(false);

      // 清除课程信息
      setQuestInfo({});
      setQuest(null);
      setSubject(null);

      // 设置加载状态
      setLoading(true);
    };

    if ((user || isGuestMode) && quest_id && chapter_id) {
      clearPreviousState();
      fetchQuset(); // fetchProgress 现在在 fetchQuest 内部调用
      checkShowBetterExperienceTip();
    } else if (user && practice_id) {
      clearPreviousState();
      fetchPractice();
      checkShowBetterExperienceTip();
    }
  }, [user, isGuestMode, practice_id, quest_id, chapter_id, lesson_id, type]);

  // 学习时长追踪
  useEffect(() => {
    if (!quest_id || !user?.id) {
      return;
    }

    const interval = setInterval(() => {
      const questName = questInfo?.title || '';

      UserTrackService.trackLearnDuration({
        user_id: user.id,
        quest_id: quest_id,
        quest_name: questName,
        learn_duration_seconds: 60
      });
    }, 60000);

    setTrackingInterval(interval);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [quest_id, user?.id, questInfo?.title]);

  // 清理追踪定时器
  useEffect(() => {
    return () => {
      if (trackingInterval) {
        clearInterval(trackingInterval);
      }
    };
  }, [trackingInterval]);

  // ==================== 数据获取方法 ====================

  // 完成练习并显示任务完成提示
  const finishPractice = async (correctRate: number) => {
    // 在review模式下不给奖励
    if (reviewMode) {
      console.log('🔄 Review mode: skipping rewards and streak update');
      return;
    }

    if (isPublicSkillPractice) {
      return;
    }

    if (!user) {
      return;
    }

    let tasks: Task[] = [];
    tasks.push(...await finishLesson(quest!, chapter_id!, lesson_id!, type === 'challenge' ? ModeType.CHALLENGE : type === 'problemSet' ? ModeType.PROBLEM_SET : ModeType.PRACTICE, correctRate));
    if (modeType === ModeType.MISTAKE) {
      tasks.push(...await finishMistakesPractice(subject!));
    }
    if (tasks.length > 0) {
      showTaskToast(tasks);
    }
    if (await completeToday()) {
      //TODO: play animation
    }

    // Create feed activity for practice completion
    createFeedActivity({
      feedType: 'practice_completed',
      contentId: lesson_id || chapter_id,
      metadata: {
        title: questInfo?.title || 'Practice',
        questTitle: quest?.title || '',
        coverImage: quest?.cover_image || '',
      }
    }, supabase).catch(error => {
      console.error('[Feed] Failed to create practice completion activity:', error);
    });
  }

  // 获取练习数据（错题回顾模式）
  const fetchPractice = async () => {
    const practice = await getPracticeById(practice_id);
    let allQuizQuestions = practice?.questions || [];
    let isFinished = practice?.progress === 100;
    let titleName = capitalizeFirstLetter(practice?.subject || '');
    let title = "Mistakes Review"
    setQuest(null);
    setSubject(practice?.subject || '');
    setQuizQuestions(allQuizQuestions);
    setInitialProgress(practice?.progress || 0);
    setInitialOptions(transferAnswersToAnswerRecords(practice?.answers || []));
    setAllFinished(isFinished);
    setQuestInfo({
      image: '',
      colorTheme: '',
      title: title,
      titleName: titleName,
    });
    setLoading(false);
  }

  const fetchAllQuizQustions = async (questions: any[]) => {
    if (!questions || questions.length === 0) {
      return [];
    }
    // 判断是否是 id 数组（字符串数组）
    const isIdArray = Array.isArray(questions) &&
      questions.length > 0 &&
      typeof questions[0] === 'string';

    if (isIdArray) {
      // 如果是 id 数组，需要根据 id 获取完整的 question 数据
      // TODO: 这里需要实现根据 id 获取 question 的逻辑
      console.log('Questions is id array, need to fetch full question data');
      return await getQuestionsByIds(questions);
    } else {
      // 如果是完整的 question 对象数组
      return questions.map((question: any) => {
        return {
          ...question,
          type: question?.type || QuestionType.MULTIPLE_CHOICE
        }
      });
    }
  }

  // 获取任务和章节数据
  const fetchQuset = async () => {
    // Run isFinished check in parallel with getQuestById —
    // these only need URL params (user.id, lesson_id, chapter_id), not quest data.
    let isFinishedPromise: Promise<boolean> = Promise.resolve(false);
    if (!isPublicSkillPractice && user) {
      if (lesson_id) {
        const modeType = type === 'challenge' ? ModeType.CHALLENGE :
                         type === 'problemSet' ? ModeType.PROBLEM_SET : ModeType.PRACTICE;
        isFinishedPromise = UserQuestProgressService.isLessonFinished(user.id, lesson_id, modeType);
      } else if (chapter_id) {
        isFinishedPromise = UserQuestProgressService.isChapterFinished(user.id, chapter_id);
      }
    }

    const [quest, isFinished] = await Promise.all([
      getQuestById(quest_id, chapter_id, lesson_id),
      isFinishedPromise,
    ]);

    const chapter = quest?.chapters?.find((chapter: Chapter) => chapter.id === chapter_id);

    let allQuizQuestions: PracticeQuestion[] = [];
    let title;
    let titleName;
    /**
     * 通过不同的渠道的获取题目内容
     * 1. 通过 lesson 获取题目内容
     *    通过取lesson.questions 获取题目内容，如果没有这个对象，则改为从所有element中过滤
     *    type默认改为multiple_choice
     * 2. 通过 chapter 获取题目内容
     *    通过取chapter.questions 获取题目内容，如果没有这个对象，则改为从所有element中过滤
     *    type默认改为multiple_choice
     */
    if (isPublicSkillPractice) {
      title = publicSkillTitle || 'Skill Practice';
      titleName = '';
      allQuizQuestions = (publicSkillQuestions || []).map((question) => ({
        ...question,
        wasWrong: false,
      }));
    } else if (lesson_id) {
      const lesson = chapter?.lesson_list.find((lesson: Lesson) => lesson.id === lesson_id);
      title = lesson?.title;
      titleName = chapter?.title;

      if (type === 'challenge') {
        allQuizQuestions = await fetchAllQuizQustions(lesson?.challenge);
      } else if (type === 'problemSet') {
        allQuizQuestions = lesson?.textbook_exercises?.map((question: any) => {
          return {
            ...question,
            type: question?.type || QuestionType.MULTIPLE_CHOICE
          }
        });
      } else if (lesson?.questions?.length > 0) {
        allQuizQuestions = await fetchAllQuizQustions(lesson?.questions);
      }
    } else {
      title = chapter?.title;
      titleName = chapter?.title;
      allQuizQuestions = await fetchAllQuizQustions(chapter?.questions);
    }

    if (!allQuizQuestions || allQuizQuestions.length === 0) {
      throw new Error('No questions found');
    }

    allQuizQuestions = allQuizQuestions.map((q: any) => ({
      ...q,
      wasWrong: false
    }));
    // Free-tier subject gate — skip only for true unauthenticated guests
    if (quest?.subject && !isGuestMode && !isPublicSkillPractice) {
      const access = await checkSubjectAccess(quest.subject);
      if (access === 'needs_selection') {
        setConfirmSubjectBeforeStart(quest.subject);
        setLoading(false);
        return;
      }
      if (access === 'blocked') {
        setSubjectPaywallSubject(quest.subject);
        setLoading(false);
        return;
      }
    }

    setQuest(quest);
    setSubject(quest?.subject || '');
    setQuizQuestions(allQuizQuestions);
    setAllFinished(isFinished);
    setQuestInfo({
      image: quest.cover_image,
      colorTheme: quest?.primary_color_theme,
      title: title,
      titleName: titleName,
      avatarUrl: quest?.instructor_profile?.avatar_url,
    });
    // setMaxProgressIndex(0);
    if (user && !isPublicSkillPractice) {
      const taskPracticeId = await startPractice();
      setTaskPracticeId(taskPracticeId);
    } else {
      setTaskPracticeId(null);
    }

    // 【迁移重点】在 questions 加载完成后，从 user_answers 表获取答案
    if (user && !isPublicSkillPractice) {
      await fetchProgress(allQuizQuestions);
    } else {
      setInitialProgress(0);
      setInitialOptions([]);
      setMaxProgressIndex(0);
    }

    setLoading(false);
  }

  // 获取用户进度数据
  const fetchProgress = async (questions: PracticeQuestion[] = []) => {
    if (isPublicSkillPractice || !user?.id || !quest_id || !chapter_id) {
      return;
    }

    const progress = await UserQuestProgressService.getProgress({
      user_id: user.id,
      quest_id: quest_id,
      chapter_id: chapter_id,
      lesson_id: lesson_id || '',
      mode_type: type === 'challenge' ? ModeType.CHALLENGE : type === 'problemSet' ? ModeType.PROBLEM_SET : ModeType.PRACTICE,
    });
    setInitialProgress(progress?.progress || 0);

    // 【迁移重点】从 user_answers 表读取答案（替代 user_option）
    if (questions && questions.length > 0) {
      const questionIds = questions.map(q => q.id);
      const modeType = type === 'challenge' ? ModeType.CHALLENGE : type === 'problemSet' ? ModeType.PROBLEM_SET : ModeType.PRACTICE;
      const placeId = lesson_id || chapter_id;

      const answers = await fetchAnswersFromUserAnswers(
        user.id,
        questionIds,
        modeType,
        placeId
      );

      console.log(`📊 Fetched ${answers.length} answers from user_answers table for ${questionIds.length} questions`);
      setInitialOptions(answers);
    } else {
      // 【警告】questions 未加载时无法从 user_answers 读取，使用空数组
      console.warn('⚠️ fetchProgress called without questions - cannot load from user_answers table');
      setInitialOptions([]);
    }

    setMaxProgressIndex(progress?.progress || 0);
  }

  // 更新用户进度到数据库
  const updateUserProgress = async (progress: number = 100) => {
    if (isPublicSkillPractice || !user?.id || !quest_id || !chapter_id) {
      return;
    }

    // 在review模式下不更新进度
    if (reviewMode) {
      console.log('🔄 Review mode: skipping progress sync to database');
      return;
    }

    try {
      await UserQuestProgressService.upsertProgress({
        user_id: user.id,
        quest_id: quest_id,
        chapter_id: chapter_id,
        lesson_id: lesson_id || null,
        element_id: 'practice_completion',
        mode_type: type === 'challenge' ? ModeType.CHALLENGE : type === 'problemSet' ? ModeType.PROBLEM_SET : ModeType.PRACTICE,
        progress: progress
      });
      console.log('User quest progress updated successfully');
    } catch (error) {
      console.error('Failed to update user quest progress:', error);
    }
  }

  // 更新用户答案到数据库
  const updateUserAnswer = async (answer: AnswerRecord[]) => {
    if (isPublicSkillPractice || !user?.id || !quest_id || !chapter_id) {
      return;
    }

    // 在review模式下不更新答案
    if (reviewMode) {
      console.log('🔄 Review mode: skipping answer sync to database');
      return;
    }

    try {
      await UserQuestProgressService.upsertAnswer({
        user_id: user.id,
        quest_id: quest_id,
        chapter_id: chapter_id,
        lesson_id: lesson_id || null,
        mode_type: type === 'challenge' ? ModeType.CHALLENGE : type === 'problemSet' ? ModeType.PROBLEM_SET : ModeType.PRACTICE,
        element_id: 'practice_completion',
        user_option: answer,
      });
    } catch (error) {
      console.error('Failed to update user quest progress:', error);
    }
  }

  // 获取钻石动画起止位置
  const getDiamondPositions = () => {
    return {
      startX: currentDiamondImageRef?.current?.getBoundingClientRect().left,
      startY: currentDiamondImageRef?.current?.getBoundingClientRect().top,
      endX: totalDiamondImageRef?.current?.getBoundingClientRect().left,
      endY: totalDiamondImageRef?.current?.getBoundingClientRect().top,
    };
  };

  // 处理答题奖励和惩罚
  const doReward = (correct: boolean) => {
    if (reviewMode) {
      console.log('🔄 Review mode: skipping reward');
      return;
    }

    if (user && taskPracticeId) {
      answerPractice(taskPracticeId, correct);
    }
    if (correct) {
      const { startX, startY, endX, endY } = getDiamondPositions();
      diamondFloatRef.current?.play();
      runDiamondAnimation({
        start: { x: startX, y: startY },
        end: { x: endX, y: endY },
      });
      // 连对3题：2*2，连对5题：3*2，连对七题：4*2
      const multiplier = comboCount >= 7 ? 4 : comboCount >= 5 ? 3 : comboCount >= 3 ? 2 : 1;
      console.log('comboCount:', comboCount);
      console.log('multiplier:', multiplier);
      addDiamondByElementType(ElementType.QUESTION, type === 'challenge', multiplier);
    } else {
      costHeart()
    }
  }

  // 处理聊天回复
  const handleChat = async (input: string) => {
    if (requestAuthPrompt('ask-pengi')) {
      return;
    }

    try {

      setShowInput(false);
      // 将用户输入添加到消息列表
      const userMessage: PracticeMessage = {
        role: 'user',
        content: input,
      }

      const newMessages = [...messages, userMessage];
      setMessages(newMessages);

      // 准备练习信息
      const practiceInfo = {
        questionId: currentQuestion?.id,
        questionType: currentQuestion?.type,
        subject: subject || quest?.subject || '',
        question: currentQuestion?.question,
        correctAnswer: currentQuestion?.answer,
      };

      // 准备学生答案信息
      const studentAnswer = {
        answer: currentAnswer.selectedOption || pendingUserInput,
        isCorrect: currentAnswer.isCorrect !== null ? currentAnswer.isCorrect : false,
        attemptNumber: answerRecords.filter(r => r.questionId === currentQuestion?.id).length + 1,
      };

      // 调用chat接口
      const response = await apiClient.post('/api/tutor-for-practice', {
        questionInfo: practiceInfo,
        studentAnswer: studentAnswer,
        messages: newMessages,
        sessionId: `practice-${quest_id}-${chapter_id}${lesson_id ? `-${lesson_id}` : ''}-${currentQuestion?.id}`,
        hasAnswered: currentAnswer.isCorrect !== null,
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }

      const data = await response.json();

      // 添加助手回复到消息列表
      const assistantMessage: PracticeMessage = {
        role: 'assistant',
        content: data.response || data.content || 'I can help you with this problem. What would you like to know?',
      }

      if (assistantMessage.content.trim() !== '') {
        playTTS(assistantMessage.content);
      }

      const updatedMessages = [...newMessages, assistantMessage];
      setMessages(updatedMessages);

      setTimeout(() => {
        if (messagesRef.current) {
          scrollToBottom();
        }
      }, 300);  

      setTimeout(() => {
        setShowInput(true);
      }, 1000);

      // 保存对话记录到数据库（review模式下不保存）
      if (!reviewMode) {
        const { data: existingChat } = await supabase
          .from('user_practice_chat')
          .select('id')
          .eq('user_id', user?.id || '')
          .eq('question_id', currentQuestion?.id || '')
          .eq('quest_id', quest_id || '')
          .eq('chapter_id', chapter_id || '')
          .eq('lesson_id', lesson_id || '')
          .maybeSingle();

        if (existingChat) {
          // 更新现有记录
          const { error } = await supabase
            .from('user_practice_chat')
            .update({
              messages: updatedMessages,
            })
            .eq('id', existingChat.id);

          if (error) {
            console.error('Failed to update chat messages:', error);
          }
        } else {
          // 插入新记录
          const { error } = await supabase
            .from('user_practice_chat')
            .insert({
              user_id: user?.id,
              question_id: currentQuestion?.id,
              quest_id: quest_id || null,
              chapter_id: chapter_id || null,
              lesson_id: lesson_id || null,
              messages: updatedMessages,
            });

          if (error) {
            console.error('Failed to insert chat messages:', error);
          }
        }
      } else {
        console.log('🔄 Review mode: skipping chat save to database');
      }

    } catch (error) {
      console.error('Error in handleChat:', error);
      // 显示错误消息给用户
      const errorMessage: PracticeMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again or refresh the page.',
      }
      setMessages(prev => [...prev, errorMessage]);
    }
  }

  if (!questInfo || !quizQuestions || quizQuestions.length === 0 || loading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <div className="text-3xl emphasize-text" style={{ color: colors.loadingColor }}>Loading...</div>
      </div>
    );
  }

  return (
    <PublicStudyAuthPromptProvider requestAuthPrompt={requestAuthPrompt}>
    <SubjectManagementModal
      visible={!!subjectPaywallSubject}
      onClose={() => { setSubjectPaywallSubject(null); router.push('/quest'); }}
    />
    <ConfirmSubjectSelectionModal
      visible={!!confirmSubjectBeforeStart}
      subject={confirmSubjectBeforeStart}
      currentSubject={profile?.free_subject ?? null}
      onClose={() => {
        setConfirmSubjectBeforeStart(null);
        router.push('/quest');
      }}
      onViewDetails={() => {
        setSubjectPaywallSubject(confirmSubjectBeforeStart);
        setConfirmSubjectBeforeStart(null);
      }}
      onConfirmed={async () => {
        setConfirmSubjectBeforeStart(null);
        router.refresh();
      }}
    />
    <div className="w-full min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.homeBgColor }}>
      <div className="learn-main-container flex flex-col min-h-screen inset-y-0 items-center">
        {/* Fixed Header */}
        <div className="fixed top-0 left-0 right-0 z-50 flex justify-center" style={{ backgroundColor: colors.mainBgColor }}>
          <Header
            showAllChapters={showAllChapters}
            setShowAllChapters={setShowAllChapters}
            currentProgress={((maxProgressIndex) / quizQuestions.length)}
            totalDiamondImageRef={totalDiamondImageRef}
            cover_image={quest?.cover_image!}
            chapter_title={isPublicSkillPractice ? '' : questInfo.titleName}
            lesson_title={isPublicSkillPractice ? (publicSkillTitle || questInfo.title) : questInfo.title}
            showHeart={Boolean(user)}
            questId={quest_id}
            animatedBar={true}
            comboCount={comboCount}
            from={from}
            modeType={modeType}
            hideTableOfContents={isPublicSkillPractice}
            hideChapterTitle={isPublicSkillPractice}
            hideCoverImage={isPublicSkillPractice}
            previewOnly={Boolean(isGuestMode || isParentPreviewMode)}
            initialChapterId={chapter_id}
            initialLessonId={lesson_id}
            onAuthRequired={isGuestMode || isParentPreviewMode ? () => requestAuthPrompt('complete-question') : undefined}
          />
        </div>

        {/* Redo Mode Indicator */}
        {reviewMode && !(showSummary && allFinished) && (
          <div className="fixed left-0 right-0 z-40 flex justify-center transition-all" style={{ top: betterExperienceTip ? '160px' : '110px' }}>
            <div className="bg-gray-500 text-white px-6 py-2 rounded-full flex items-center gap-3 animate-fade-in border-2 border-white shadow-lg">
              <span className="text-lg emphasize-text">Practice Review Mode</span>
            </div>
          </div>
        )}

        {/* Scrollable Content Area with proper spacing */}
        <div className="w-full relative" style={{ paddingTop: reviewMode ? (betterExperienceTip ? '200px' : '150px') : (betterExperienceTip ? `${baseHeight + 30}px` : `${baseHeight}px`), paddingBottom: '80px' }}>
          <div className="flex flex-col learn-main-container" style={{ backgroundColor: colors.homeBgColor, minHeight: betterExperienceTip ? 'calc(100vh - 230px)' : 'calc(100vh - 180px)' }}>
            <div className="flex-1 flex justify-center pb-15 pt-4">
              <div className="flex flex-col items-center w-full">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={showMistakeTransition ? 'transition' : (currentQuestion?.id || currentQuestionIndex)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.3,
                      ease: "easeInOut"
                    }}
                    className="w-full flex flex-col items-center "
                  >
                    {showMistakeTransition ? (
                      // 错题过渡页面
                      <div className="w-full md:w-3/4 flex flex-col bg-white rounded-2xl shadow-lg z-20">
                        <div className="flex flex-row items-center gap-2 p-4">
                          {
                            questInfo?.avatarUrl && (
                              <Image 
                                src={questInfo?.avatarUrl}
                                alt="Assistant Avatar" 
                                className="rounded-full object-contain" 
                                width={32} 
                                height={32} 
                                optimizedWidth={80}
                              />
                            )
                          }
                          <div className="text-xl emphasize-text" style={{ color: colors.mainTitleColor }}>Pengi</div>
                        </div>
                        <div className="relative w-full h-[300px] md:h-[400px] lg:h-[450px] overflow-hidden">
                          <div className="absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%] flex flex-row items-start justify-center gap-2">
                            <Image 
                              src="/assets/practice_middle_text.png" 
                              alt="practice_middle_text" width={172} height={59} 
                              className="w-[300px] h-[100px] object-contain" 
                              optimizedWidth={600}
                            />
                            <Image 
                              src="/assets/practice_middle.png" 
                              alt="practice_middle" width={83} height={97} 
                              className="md:w-[140px] md:h-[170px] w-[100px] h-[120px] object-contain" 
                              optimizedWidth={200}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <PracticeNode
                      // Display state
                      currentQuestion={currentQuestion}
                      currentAnswer={currentAnswer}
                      showSummary={showSummary}
                      reviewAnswer={reviewAnswer}
                      showReportPopup={showReportPopup}
                      wasFirstAnswerWrong={currentQuestionWasWrongInitially}
                      showRetryLiveTutor={shouldShowRetryLiveTutor}

                      // Question data
                      quizQuestions={quizQuestions}
                      answerRecords={answerRecords}

                      // Progress
                      allFinished={allFinished}

                      // Identifiers
                      questId={quest_id}
                      chapterId={chapter_id}
                      lessonId={lesson_id}
                      lessonTitle={questInfo?.title}
                      modeType={modeType}
                      avatarUrl={questInfo?.avatarUrl}
                      subject={subject || quest?.subject}

                      // Handlers
                      handleAnswer={handleAnswer}
                      handleReviewAnswers={handleReviewAnswers}
                      handleReviewClose={handleReviewClose}
                      setShowReportPopup={setShowReportPopup}
                      setShowAllChapters={setShowAllChapters}
                      onInputChange={setPendingUserInput}
                      triggerCheck={triggerCheck}

                      // Refs
                      currentDiamondImageRef={currentDiamondImageRef}
                      diamondFloatRef={diamondFloatRef}

                      // Chat state
                      isChatActive={showInput}

                      // Combo count
                      comboCount={comboCount}

                      canReview={canReview}
                      reviewMode={reviewMode}
                      setShowReviewModal={setShowReviewModal}
                      onRequireAuth={requestAuthPrompt}
                    />
                    )}
                  </motion.div>
                </AnimatePresence>
                {/* Chat Messages Section */}
                <div key={currentAnswer?.id} className="w-full flex flex-col items-center justify-center gap-2 mt-4" ref={messagesRef}>
                  {loadingMessages ? (
                    <div className="w-full md:w-3/4 flex justify-center items-center py-8">
                      <Loader className="w-8 h-8 animate-spin" style={{ color: colors.mainTitleColor }} />
                    </div>
                  ) : messages.length > 0 && (
                    <div className="w-full md:w-3/4 flex flex-col gap-2">
                      {messages.map((message, index) => {
                        return (
                          <div
                            key={'practice_message_' + index}
                            className="flex flex-col w-full items-center relative"
                          >
                            <div className="flex flex-col bg-white w-full border-1 border-gray-300 rounded-xl p-4">
                              <div className="flex flex-row justify-between">
                                <div className="flex flex-row items-center gap-2">
                                  {message.role === 'user' ? (
                                    <>
                                      <LetterAvatar backgroundColor={'#805ad5'} />
                                      <span className="text-lg emphasize-text" style={{ color: colors.mainTitleColor }}>
                                        You
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      {questInfo?.avatarUrl ? (
                                        <Image
                                          src={questInfo.avatarUrl}
                                          alt="Assistant Avatar"
                                          className="rounded-full object-contain"
                                          width={32}
                                          height={32}
                                          optimizedWidth={80}
                                        />
                                      ) : (
                                        <AssistantAvatar />
                                      )}
                                      <span className="text-lg emphasize-text" style={{ color: colors.mainTitleColor }}>
                                        {quest?.instructor_profile?.name || 'Pengi'}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>

                              <div className="rounded-lg p-2 px-8" style={{ fontSize: 14, fontWeight: 500, color: colors.mainTextColor }}>
                                <MarkdownComponent content={message.content || ''} />
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Thinking indicator */}
                      {messages.length > 0 && messages[messages.length - 1].role === 'user' && (
                        <div className="flex flex-row items-center gap-2 bg-white w-full border-1 border-gray-300 rounded-xl p-4 animate-fade-in">
                          {questInfo?.avatarUrl ? (
                            <Image
                              src={questInfo.avatarUrl}
                              alt="Assistant Avatar"
                              className="rounded-full object-contain"
                              width={32}
                              height={32}
                              optimizedWidth={80}
                            />
                          ) : (
                            <AssistantAvatar />
                          )}
                          <span className="text-lg emphasize-text animate-pulse" style={{ color: colors.mainTextColor }}>
                            Thinking...
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Input Section */}
                  {showInput && (
                    <div className="w-full flex flex-row justify-center items-center mt-2">
                      <div className="w-full md:w-3/4 flex flex-row justify-center items-center">
                        <LearnInput 
                          handleSubmit={(input) => {
                            if (input.trim().length === 0) {
                              return;
                            }
                            handleChat(input);
                          }}
                          onClose={() => setShowInput(false)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Footer Button Area */}
        {
          (!reviewAnswer && !showSummary) && (
            <div
              className="fixed left-0 right-0 z-40 flex justify-center transition-all duration-200"
              style={{
                backgroundColor: colors.homeBgColor,
                bottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0'
              }}
            >
              <div className="learn-main-container flex flex-col justify-center items-center">
                <div className="w-full sm:w-3/4 items-center justify-center flex flex-row gap-4 px-4" style={{ height: '80px' }}>
                  {!practice_id && (
                    <div className="relative w-[80px] lg:w-[180px] flex flex-col items-center justify-center">
                      <div className="w-[42px] hidden lg:block lg:w-full h-[42px] lg:h-[48px]">
                    <NormalButton
                      onClick={() => {
                            if (requestAuthPrompt('ask-pengi')) {
                              return;
                            }
                            report(ReportEvent.PRACTICE_ASK_PENGI_BUTTON, {
                              quest_id: quest_id,
                              chapter_id: chapter_id,
                              lesson_id: lesson_id,
                              question_id: currentQuestion?.id,
                            });
                            setShowInput(true);
                          }}
                          className="mt-[-2px] relative"
                          textClassName="py-[9px] text-2xl"
                        >
                          Ask Pengi
                        </NormalButton>
                      </div>
                      <button 
                        onClick={() => {
                          if (requestAuthPrompt('ask-pengi')) {
                            return;
                          }
                          interruptAudio();
                          setShowInput(true);
                        }}
                        className="absolute -top-[60px] left-[10px] lg:-top-[110px] lg:left-[30px]"
                      >
                        <Image
                          src={'/assets/ask_pengi_1.png'}
                          alt="ask_pengi_2"
                          className="w-[65px] lg:w-[120px] h-[80px] lg:h-[140px]"
                          width={89}
                          height={105}
                          style={{
                            pointerEvents: 'none',
                          }}
                          optimizedWidth={200}
                        />
                      </button>
                    </div>
                  )}
                  <div className="flex-1">
                    <NormalButton
                      onClick={() => {
                        console.log('123132123');
                        // 当 heart <= 0 时，拦截点击事件并显示购买弹窗
                        if (reward.heart <= 0) {
                          useRewardStore.setState({ buyHeartPopup: true });
                          return;
                        }
                        handleNext();
                      }}
                      disabled={isNextButtonDisabled()}
                      colorType={getButtonColorType()}
                      textClassName="py-[9px] text-2xl"
                    >
                      {getButtonText()}
                    </NormalButton>
                  </div>
                </div>
              </div>
            </div>
          )
        }
        <Suspense fallback={null}>
          <HeartBuyPopup
            visible={buyHeartPopup}
            onClose={() => useRewardStore.setState({ buyHeartPopup: false })}
          />
        </Suspense>
        <Suspense fallback={null}>
          <CustomFeedbackSurvey />
        </Suspense>
        <PublicStudyAuthPromptModal
          visible={showAuthPrompt}
          reason={authPromptReason}
          onClose={() => setShowAuthPrompt(false)}
        />
        <Suspense fallback={null}>
          <ReviewPracticeModal
            visible={showReviewModal}
            onClose={() => setShowReviewModal(false)}
            onNextClicked={() => {
              report(ReportEvent.PRACTICE_REVIEW_MODE_START, {
                quest_id: quest_id,
                chapter_id: chapter_id,
                lesson_id: lesson_id,
              });
              enterReviewMode();
              setShowReviewModal(false);
            }}
          />
        </Suspense>

        {/* 庆祝动画 - 屏幕中心 */}
        <AnimatePresence>
          {showCelebration && celebrationAnimationData && (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{
                duration: 0.3,
                ease: 'easeOut'
              }}
              className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[200]"
              style={{
                width: '240px',
                height: '240px',
                pointerEvents: 'none'
              }}
            >
              <CanvasPlayerMini
                canvasData={celebrationAnimationData}
                autoPlay={true}
                loop={false}
                className="w-full h-full"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
    </PublicStudyAuthPromptProvider>
  )
}

// 主页面组件
export default function Practice(props: PracticeClientProps) {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <PracticeContent {...props} />
    </Suspense>
  );
}
